import React, { useEffect, useState, useCallback } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

interface RecaptchaVerifierProps {
  onVerified: (token: string) => void;
  action?: string;
  retryText?: string;
  recaptchaToken?: string | null;
}

export const RecaptchaVerifier: React.FC<RecaptchaVerifierProps> = ({
  onVerified,
  recaptchaToken = null,
  action = "form_submit",
  retryText = "We couldn't verify you. Please try again."
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [verifying, setVerifying] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState("");
  const [ready, setReady] = useState(false);

  const verifyCaptcha = useCallback(async () => {
    if (!executeRecaptcha) {
      setReady(false);
      return;
    }

    setReady(true);
    setVerifying(true);
    setRecaptchaError("");

    try {
      const token = await executeRecaptcha(action);

      if (!token) {
        throw new Error("Token was not returned from reCAPTCHA.");
      }

      onVerified(token);
    } catch (err) {
      setRecaptchaError("Verification failed. Please try again.");
    } finally {
      setVerifying(false);
    }
  }, [executeRecaptcha, action, onVerified]);

  // Automatically verify on component mount
  useEffect(() => {
    if (!recaptchaToken) {
      verifyCaptcha();
    }
  }, [verifyCaptcha, recaptchaToken]);

  return !recaptchaToken ? (
    <div style={{ marginTop: "1rem" }}>
      {verifying && <span style={{ fontStyle: "italic" }}>Verifying you're human... ⏳ Please complete the reCAPTCHA.</span>}
      {!ready && <span style={{ fontStyle: "italic" }}>reCAPTCHA not ready. Please refresh or wait.</span>}
      {recaptchaError && <div style={{ color: "red", marginBottom: "0.5rem" }}>{recaptchaError}</div>}

      {!verifying && recaptchaError && (
        <button
          type="button"
          onClick={verifyCaptcha}
          style={{
            padding: "8px 16px",
            backgroundColor: "#f44336",
            color: "#fff",
            border: "none",
            borderRadius: 4,
            cursor: "pointer"
          }}
        >
          {retryText}
        </button>
      )}
    </div>
  ) : (<></>);
};
